<script setup lang="ts">
import { useTemplateRef, computed } from 'vue';
import { useHover } from '@/composables/useHover';
import Icon from '@/components/Icon.vue';
import Markup from '@/components/Markup.vue';

interface HeroCardProps {
    title: string;
    description?: string;
}

withDefaults(defineProps<HeroCardProps>(), {
    description: '',
});

const cardRef = useTemplateRef('card');
const { isHovered } = useHover(cardRef);

const cardClass = computed(() => ({
    // keep a fixed width for stability (tweak as you like)
    'bg-gray-600/60 shadow': !isHovered.value,
    'bg-gray-600/80 shadow-lg -translate-y-0.5': isHovered.value,
}));
</script>

<template>
    <div class="w-full h-full relative">
        <div
            ref="card"
            class="absolute z-30 w-full rounded-[30px] backdrop-saturate-150 flex flex-col items-center justify-start px-4 py-3 transform-gpu translate-z-0 transition-[transform,opacity,box-shadow,background-color] duration-250 ease-out will-change-[transform,opacity] shadow-md shadow-gray-300/30"
            :class="cardClass"
        >
            <!-- put blur on its own layer so it doesn't repaint with every change -->
            <div
                class="pointer-events-none absolute inset-0 rounded-[30px] backdrop-blur-md"
                aria-hidden="true"
            />

            <h3 class="relative text-white text-2xl font-semibold flex items-center gap-2">
                <Icon name="info-outline" class="mr-1 transition-opacity duration-200" />
                {{ title }}
            </h3>

            <!-- Smooth reveal: grid-rows + fade + tiny translate -->
            <div
                class="grid w-full transition-[grid-template-rows,margin,opacity,transform] duration-250 ease-out"
                :class="
                    isHovered
                        ? 'grid-rows-[1fr] mt-0 opacity-100 -translate-y-1'
                        : 'grid-rows-[0fr] mt-0 opacity-0 -translate-y-0.5'
                "
            >
                <div class="overflow-hidden">
                    <Markup
                        :content="description"
                        textColor="light"
                        textSize="lg"
                        class="text-white leading-none"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
